'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Check, TriangleAlert, X } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp } from './demoParts'

// GHL setup: the parts the page lists, set up alone (stuck on the three it
// names: pipeline logic, A2P and domain authentication) versus set up by GHL
// Prime. The account-health ring fills as each part is checked.

const PARTS = [
  'Sub-account architecture',
  'Pipelines that mirror how you sell',
  'Calendars and booking',
  'A2P 10DLC registration',
  'Phone + email domain authentication',
  'Data migrated cleanly',
  'Packaged as a reusable snapshot',
]
const PATHS = {
  alone: {
    states: [
      ['ok', 'Done'],
      ['warn', 'Generic stages'],
      ['ok', 'Done'],
      ['bad', 'Stuck: rejected'],
      ['bad', 'Missing: emails to spam'],
      ['warn', 'Half imported'],
      ['bad', 'Not started'],
    ],
    score: 43,
    result: { tone: 'bad', text: 'Leads leak · messages filtered' },
  },
  prime: {
    states: PARTS.map(() => ['ok', 'Done']),
    score: 100,
    result: { tone: 'good', text: 'Every lead lands · every message delivered' },
  },
}
const DELAYS = [400, 650, 650, 650, 650, 650, 650, 650, 600]
const R = 52
const C = 2 * Math.PI * R
const WEIGHT = { ok: 1, warn: 0.5, bad: 0 }

export default function SetupBuild() {
  const [ref, onScreen] = useOnScreen()
  const [path, setPath] = useState('alone')
  const [run, setRun] = useState(0)
  const [auto, setAuto] = useState(true)
  const data = PATHS[path]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${path}-${run}` })
  const finished = step >= DELAYS.length
  const checked = Math.max(0, Math.min(PARTS.length, step - 1))
  const raw = data.states.slice(0, checked).reduce((sum, [state]) => sum + WEIGHT[state], 0)
  const score = finished ? data.score : Math.round((raw / PARTS.length) * 100)

  useEffect(() => {
    if (!auto || path !== 'alone' || !finished) return undefined
    const timer = setTimeout(() => setPath('prime'), 1800)
    return () => clearTimeout(timer)
  }, [auto, path, finished])

  const pick = (key) => { setAuto(false); setPath(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-setup"
      title="Account health check"
      label="Demo: a GoHighLevel account set up alone versus set up by GHL Prime, scored part by part"
      tabs={[{ key: 'alone', label: 'Set up alone', tone: 'bad' }, { key: 'prime', label: 'Set up by GHL Prime', tone: 'good' }]}
      activeTab={path}
      onTab={pick}
      onReplay={() => pick(path)}
    >
      <div className="svd-setup-grid">
        <div className={`svd-ring is-${score >= 90 ? 'good' : score >= 60 ? 'wait' : 'bad'}`} aria-label={`Account health ${score}%`}>
          <svg viewBox="0 0 120 120" aria-hidden="true">
            <circle cx="60" cy="60" r={R} className="svd-ring-bg" />
            <motion.circle cx="60" cy="60" r={R} className="svd-ring-fill" strokeDasharray={C} animate={{ strokeDashoffset: C * (1 - score / 100) }} transition={{ duration: 0.6, ease: 'easeOut' }} />
          </svg>
          <span><b className="svd-3d-num is-small">{score}%</b><small>account health</small></span>
        </div>
        <ul className="svd-parts">
          {PARTS.map((part, i) => {
            const [state, note] = data.states[i]
            const shown = i < checked
            return (
              <li key={`${path}-${part}`} className={shown ? `is-${state}` : ''}>
                <span className="svd-part-mark" aria-hidden="true">
                  {shown ? (state === 'ok' ? <Check size={11} strokeWidth={3} /> : state === 'warn' ? <TriangleAlert size={11} /> : <X size={11} strokeWidth={3} />) : null}
                </span>
                <span className="svd-part-name">{part}</span>
                {shown ? <motion.span className="svd-part-note" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{note}</motion.span> : null}
              </li>
            )
          })}
        </ul>
      </div>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${path}-${run}`} />
    </DemoFrame>
  )
}
